import { useMemo } from "react";
import { ABBREVIATED_OPTION, useConfig } from "@/hooks/useConfig";
import { formatDate } from "@/pages/scenario-builder/utils";
import { isDefined } from "@/Utils/array-utils";

import { formatPercentageFromFraction } from "@/pages/scenario-builder/v3/utils/formatPercentage";
import { useGeoEnabled } from "@/pages/scenario-builder/v3/hooks/useGeoEnabled";
import { ScenarioOption } from "../types";

type ScenarioField = {
  label: string;
  value: string;
};

export const useScenarioData = (selectedScenario: ScenarioOption | undefined) => {
  const { config } = useConfig();
  const geoEnabledQuery = useGeoEnabled();
  const isGeoEnabled = geoEnabledQuery.isLoading ? false : geoEnabledQuery.isEnabled;

  const summaryFormatter = (value: number) =>
    config.currency.formatCurrency(value, ABBREVIATED_OPTION);

  const scenarioFields = useMemo((): ScenarioField[] => {
    if (!selectedScenario) {
      return [
        { label: "Business Unit", value: "-" },
        ...(isGeoEnabled ? [{ label: "Geography", value: "-" }] : []),
        { label: "Timeframe", value: "-" },
        { label: "Planned Spend", value: "-" },
        { label: "Forecast Revenue", value: "-" },
        { label: "Estimated ROI", value: "-" },
      ];
    }

    const startDate = selectedScenario.startDate
      ? formatDate(new Date(selectedScenario.startDate))
      : "N/A";
    const endDate = selectedScenario.endDate
      ? formatDate(new Date(selectedScenario.endDate))
      : "N/A";
    const formattedAverageROI = isDefined(selectedScenario.estimatedROI)
      ? formatPercentageFromFraction(selectedScenario.estimatedROI)
      : "-";

    const geographyValue = isDefined(selectedScenario.geography)
      ? selectedScenario.geography
      : "All Geographies";

    return [
      { label: "Business Unit", value: selectedScenario.businessUnit ?? "-" },
      ...(isGeoEnabled ? [{ label: "Geography", value: geographyValue }] : []),
      { label: "Timeframe", value: `${startDate} - ${endDate}` },
      { label: "Planned Spend", value: summaryFormatter(selectedScenario.plannedSpend) },
      { label: "Forecast Revenue", value: summaryFormatter(selectedScenario.forecastRevenue) },
      { label: "Estimated ROI", value: formattedAverageROI },
    ];
  }, [selectedScenario, summaryFormatter, isGeoEnabled]);

  return {
    scenarioFields,
  };
};
